/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 * Parse SDK parameter and init the channel options.
 * TODO: This should be an Angular service.
 *
 * OptionManager.fromString should be called to load the options
 * @format
 */

import Logger from "../../../../core/libs/logger";

// helper variable only used in this scope.
const charcodeOf0 = "0".charCodeAt(0);
const charcodeOf9 = "9".charCodeAt(0);
const charcodeOfa = "a".charCodeAt(0);
const charcodeOfz = "z".charCodeAt(0);
const charcodeOfA = "A".charCodeAt(0);
const charcodeOfZ = "Z".charCodeAt(0);

export class OptionManager {
   private readonly maxOptionStringLength = 65535;
   private readonly maxChannelLength = 7;
   private readonly separateSign = ",";
   private readonly escapeSign = "/";
   private options = new Map<string, string>();
   private logger = new Logger(Logger.RDPVCBRIDGE);
   constructor() {}

   /**
    * Always overwrite if there is already a option for name
    * @param name The channel name
    * @param option The channel option
    * @returns This returns Whether add without any error
    */
   public add = (name: string, option: number): boolean => {
      if (!(name.length > 0)) {
         this.logger.error("invalid name" + name);
         return false;
      }
      if (!Number.isInteger(option) || option < 0 || option > 0xffffffff) {
         this.logger.error("invalid option" + option);
         return false;
      }
      // clip but continue
      if (name.length > this.maxChannelLength) {
         this.logger.warning("must clip the channel name " + name);
         name = name.substring(0, this.maxChannelLength);
      }
      this.options.set(name, String(option));
      return true;
   };

   /**
    * clear the options.
    */
   public clear = () => {
      this.options = new Map<string, string>();
   };

   /**
    * @returns This returns the recorded options in string
    */
   public toString = (): string => {
      const attributeArray = [];
      this.options.forEach((value, name) => {
         attributeArray.push(this.encode(name));
         attributeArray.push(this.encode(value));
      });
      return attributeArray.join(this.separateSign);
   };

   /**
    * Corresponding to SDK API, the validation check must be done at client.
    * Different from C SDK, we would not provide CLI API, but to keep consistent,
    * we still use encoded format, to ease the migration effort from C SDK to JS SDK.
    *
    * @param optionString the string passed from SDK, which should not be trusted
    * as "Channel name, Channel Option, Channel Name, Channel Option"
    * Where:
    * - Channel name can should only contains encoded ASCII char.
    * - Channel option is Uint32 number in dec string.
    * @returns This returns whether read without any error
    */
   public fromString = (optionString: string): boolean => {
      let validFormat = true;
      if (!optionString) {
         return validFormat;
      }

      // prevent non string and super long string.
      if (typeof optionString !== "string" || optionString.length > this.maxOptionStringLength) {
         Logger.error("invalid type for optionString");
         return false;
      }
      const splitResult = optionString.split(this.separateSign);

      // invalid, but try to continue processing
      if (splitResult.length % 2 !== 0) {
         splitResult.pop();
         validFormat = false;
      }
      this.clear();
      for (let i = 0; i < splitResult.length - 1; i += 2) {
         const name = this.decode(splitResult[i]);
         const options = this.decode(splitResult[i + 1]);
         const optionsNumber = Number(options);
         if (!this.add(name, optionsNumber)) {
            // invalid, but try to continue processing
            Logger.warning("detect invalid option " + splitResult[i] + ", for channel " + splitResult[i + 1]);
            validFormat = false;
         }
      }
      return validFormat;
   };

   private encode = (originString: string): string => {
      if (!originString) {
         return "";
      }
      const encodedString = originString
         .split("")
         .map((value) => {
            const charCode = value.charCodeAt(0);
            if (charCode > 255) {
               this.logger.error("UTF-16 option not supported");
               return [""];
            }
            if (this.canPassThrough(charCode)) {
               return [value];
            }
            const high = (0xf0 & charCode) >> 4;
            const low = 0x0f & charCode;
            return [this.escapeSign, this.escapeHex(high), this.escapeHex(low)];
         })
         .reduce((a, b) => {
            return a.concat(b);
         });
      return encodedString.join("");
   };

   // internal use only, avoid not needed check
   private canPassThrough = (charCode: number): boolean => {
      // refactor
      if (charcodeOf0 <= charCode && charCode <= charcodeOf9) {
         return true;
      }
      if (charcodeOfa <= charCode && charCode <= charcodeOfz) {
         return true;
      }
      if (charcodeOfA <= charCode && charCode <= charcodeOfZ) {
         return true;
      }
      return false;
   };

   // Internal use only, avoid not needed check
   private escapeHex = (targetNumber: number): string => {
      if (targetNumber < 10) {
         return String(targetNumber);
      }
      return String.fromCharCode(charcodeOfA + targetNumber - 10);
   };

   /**
    * code strings with escaped char to normal char string
    * @param encodedString the input string segment from SDK as option
    * @returns
    */
   private decode = (encodedString: string): string => {
      if (!encodedString) {
         return "";
      }
      let decodedString = "";
      for (let i = 0; i < encodedString.length; i++) {
         const char = encodedString[i];
         const charCode = char.charCodeAt(0);
         if (charCode > 255) {
            Logger.error("only ASCII code supported for option");
            return "";
         }
         if (char === this.escapeSign) {
            if (i + 2 >= encodedString.length) {
               Logger.error("invalid string to decode for option at end");
               return "";
            }
            const highCharCode = encodedString[++i];
            const lowCharCode = encodedString[++i];
            const unescapedCharCode = parseInt(highCharCode + lowCharCode, 16);
            if (Number.isNaN(unescapedCharCode)) {
               Logger.error("invalid escaped string for option of " + encodedString.substring(i - 2, 3));
               return "";
            }
            decodedString += String.fromCharCode(unescapedCharCode);
         } else {
            decodedString += char;
         }
      }
      return decodedString;
   };
}
